import React, { useCallback, useEffect, useState } from 'react';
import { FormattedDate } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import LoadingSpinner from 'Utils/LoadingSpinner';
import Modal from 'Utils/Modal';
import { HelpCircle } from 'Utils/SvgIcons';
import { client } from 'Utils/apollo/client';
import { displayRentalStatus } from 'Constants/displayConstants';
import {
  GetAddOnReservedOrdersDocument,
  GetAddOnReservedOrdersQuery,
} from '../../../../types/graphql';
import {
  AddOnReservedOrder,
  AddOnReservedOrdersModalProps,
} from './AddOnReservedOrdersModal.types';
import {
  EmptyState,
  ColumnContent,
  LabelBlock,
  LabelRowBottom,
  LabelRowTop,
  ModalContent,
  OrdersTable,
  OrdersTableWrapper,
  QuantityLabel,
  ViewOrdersLink,
} from './AddOnReservedOrdersModal.styles';

const MAX_ORDER_NAME_LENGTH = 23;

const RESERVED_PENDING_CHECKOUT_TOOLTIP =
  'This is the quantity that has been reserved, but is still on shelf because it has not been checked out.';

const isPastOrder = (
  eventStartDateTime?: string | Date | null
): boolean => {
  if (!eventStartDateTime) {
    return false;
  }

  const eventDate = new Date(eventStartDateTime);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  return eventDate < startOfToday;
};

const truncateOrderName = (name?: string | null): string => {
  if (!name) {
    return '-';
  }

  if (name.length <= MAX_ORDER_NAME_LENGTH) {
    return name;
  }

  return `${name.slice(0, MAX_ORDER_NAME_LENGTH)}...`;
};

const mapOrders = (
  orders: GetAddOnReservedOrdersQuery['getAddOnReservedOrders']
): AddOnReservedOrder[] =>
  orders.map((order) => ({
    token: order.token,
    name: order.name,
    status: order.status,
    eventStartDateTime: order.eventStartDateTime,
    quantity: order.quantity,
  }));

const AddOnReservedOrdersModal: React.FC<AddOnReservedOrdersModalProps> = ({
  addOnToken,
  reservedQuantity,
  quantityClassName,
  tooltipId,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [orders, setOrders] = useState<AddOnReservedOrder[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHasFetched(false);
    setOrders([]);
    setError(null);
    setLoading(false);
  }, [addOnToken]);

  const toggleModal = useCallback(() => {
    setModalOpen((open) => !open);
  }, []);

  const handleViewOrdersClick = useCallback(async () => {
    setModalOpen(true);

    if (hasFetched || !addOnToken) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, errors = [] } = await client.query<GetAddOnReservedOrdersQuery>({
        query: GetAddOnReservedOrdersDocument,
        variables: { addOnToken },
        fetchPolicy: 'cache-first',
      });

      if (errors.length) {
        throw new Error('Failed to load reserved orders.');
      }

      setOrders(mapOrders(data.getAddOnReservedOrders));
      setHasFetched(true);
    } catch (fetchError) {
      const message =
        fetchError instanceof Error
          ? fetchError.message
          : 'An error occurred while loading reserved orders.';
      setError(message);
      setOrders([]);
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, [addOnToken, hasFetched]);

  const handleRowClick = useCallback((token: string) => {
    if (token) {
      window.open(`/orders/rentals/${token}`, '_blank');
    }
  }, []);

  const getStatusLabel = (status: string): string =>
    displayRentalStatus[status as keyof typeof displayRentalStatus] ?? status;

  return (
    <div className='addonQuantityColumn'>
      <ColumnContent>
        <LabelBlock>
          <LabelRowTop>
            <strong>Reserved Pending</strong>
            <a
              className='info'
              data-tip
              data-for={tooltipId}
              data-event='mouseenter'
              data-event-off='mouseleave'
            >
              <HelpCircle />
            </a>
            <ReactTooltip
              id={tooltipId}
              className='tooltip left'
              place='right'
              effect='solid'
            >
              <p>{RESERVED_PENDING_CHECKOUT_TOOLTIP}</p>
            </ReactTooltip>
          </LabelRowTop>
          <LabelRowBottom>
            <strong>Checkout</strong>
            <ViewOrdersLink type='button' onClick={handleViewOrdersClick}>
              View Orders
            </ViewOrdersLink>
          </LabelRowBottom>
        </LabelBlock>
        <QuantityLabel className={quantityClassName}>
          {reservedQuantity}
        </QuantityLabel>
      </ColumnContent>

      <Modal open={modalOpen} toggle={toggleModal} title='Reserved Orders' large>
        <ModalContent>
          {loading && <LoadingSpinner />}
          {!loading && error && <EmptyState>{error}</EmptyState>}
          {!loading && !error && hasFetched && orders.length === 0 && (
            <EmptyState>No reserved orders found.</EmptyState>
          )}
          {!loading && !error && orders.length > 0 && (
            <OrdersTableWrapper>
              <OrdersTable>
                <colgroup>
                  <col className='token' />
                  <col className='name' />
                  <col className='date' />
                  <col className='status' />
                  <col className='quantity' />
                </colgroup>
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>Name</th>
                    <th>Event Start Date</th>
                    <th>Rental Status</th>
                    <th>Qty Reserved</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const orderName = truncateOrderName(order.name);
                    const isPast = isPastOrder(order.eventStartDateTime);

                    return (
                      <tr
                        key={order.token}
                        onClick={() => handleRowClick(order.token)}
                        className={isPast ? 'is-past' : undefined}
                      >
                        <td>{order.token}</td>
                        <td title={order.name || undefined}>{orderName}</td>
                        <td>
                          {order.eventStartDateTime ? (
                            <FormattedDate
                              value={new Date(order.eventStartDateTime)}
                              day='2-digit'
                              month='2-digit'
                              year='numeric'
                            />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{getStatusLabel(order.status)}</td>
                        <td>{order.quantity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </OrdersTable>
            </OrdersTableWrapper>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AddOnReservedOrdersModal;
